<template>
  <div class="container">
    <canvas ref="canvas"></canvas>
    <div v-if="showHint" class="hint">
      Slide your finger through the cosmic numbers to answer!
    </div>
    <div class="controls">
      <div class="math-attack-toggle">
        <label class="switch">
          <input type="checkbox" v-model="mathAttackActive" @change="handleMathAttackSwitchChange" />
          <span class="slider"></span>
        </label>
        <span class="switch-label">{{ mathAttackActive ? 'Math Attack On' : 'Math Attack Off' }}</span>
      </div>
      <transition name="fade">
        <div v-if="mathAttackActive && sessionStartTime" class="math-attack-info">
          <div class="session-time">Session Time Left: {{ sessionTimeLeft }} s</div>
          <div class="question-board">Question: {{ currentQuestion }}</div>
          <div class="progress-container">
          <div class="progress-bar" :style="{ width: questionProgress + '%' }"></div>
          <div class="progress-label">{{ questionProgress.toFixed(1) }}%</div>
        </div>
          <div class="score">Score: {{ mathAttackScore }}</div>
          <div class="highscore">High Score: {{ highScore }}</div>
          <div class="user-input">Your Answer: {{ userInput }}</div>
        </div>
      </transition>
    </div>

    <!-- Difficulty Selection Modal -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showDifficultyModal">
        <div class="modal-content">
          <h2>Select Difficulty</h2>
          <div class="difficulty-selection-modal">
            <button @click="selectDifficulty('easy')">Easy (20s)</button>
            <button @click="selectDifficulty('medium')">Medium (10s)</button>
            <button @click="selectDifficulty('hard')">Hard (7.5s)</button>
            <button @click="selectDifficulty('impossible')">Impossible (5s)</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Feature Tip Modal -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showFeatureModal">
        <div class="modal-content">
          <h2>Feature Tip</h2>
          <p>{{ currentTip }}</p>
          <div class="modal-buttons">
            <button @click="neverShowTips">Never show again</button>
            <button @click="closeModalTip">View later</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MathAttackPage',
  data() {
    return {
      canvas: null,
      ctx: null,
      hue: 0,
      particles: [],
      touchActive: false,
      showHint: true,
      animationFrame: null,
      audioContext: null,
      lastSoundTime: 0,
      gravityX: 0,
      gravityY: 0,
      // Math Attack properties
      mathAttackActive: false,
      mathAttackLevel: 'easy',
      sessionDuration: 120, // in seconds
      questionDuration: 0,
      sessionStartTime: null,
      questionStartTime: null,
      mathAttackScore: 0,
      mathTimerInterval: null,
      highScore: 0,
      currentTime: Date.now(),
      questionDelay: 1000, // 1-second delay between questions
      // Math question properties
      currentQuestion: '',
      currentAnswer: '',
      userInput: '',
      // Bubble properties
      bubbles: [],
      bubbleRadius: 40,
      loadingBubbles: false, // flag to avoid concurrent bubble updates
      // Modal Tip properties
      showFeatureModal: false,
      neverShowModal: false,
      modalContent: [
        "Tip: Tilt your device to alter the smoke direction.",
        "Tip: Slide your finger carefully over the numbers.",
        "Tip: Each correct digit adds to your cosmic score.",
        "Tip: Enjoy the blend of cosmic visuals and math challenges."
      ],
      modalIndex: 0,
      modalInterval: null,
      // Difficulty Modal property
      showDifficultyModal: false,
      // Celebration properties
      celebrationActive: false,
      celebrationParticles: []
    };
  },
  computed: {
    sessionTimeLeft() {
      if (!this.mathAttackActive || !this.sessionStartTime) return this.sessionDuration.toFixed(1);
      let elapsed = (this.currentTime - this.sessionStartTime) / 1000;
      return Math.max(0, this.sessionDuration - elapsed).toFixed(1);
    },
    questionTimeLeft() {
      if (!this.mathAttackActive || !this.questionStartTime) return this.questionDuration.toFixed(1);
      let elapsed = (this.currentTime - this.questionStartTime) / 1000;
      return Math.max(0, this.questionDuration - elapsed).toFixed(1);
    },
    questionProgress() {
      let left = parseFloat(this.questionTimeLeft);
      if (this.questionDuration === 0) return 0;
      return ((this.questionDuration - left) / this.questionDuration) * 100;
    },
    currentTip() {
      return this.modalContent[this.modalIndex];
    }
  },
  watch: {
    mathAttackActive(newVal) {
      if (!newVal) {
        this.sessionStartTime = null;
        this.clearModalTips();
        if (!this.neverShowModal) this.scheduleModalTips();
      } else {
        this.clearModalTips();
        this.showFeatureModal = false;
      }
    },
    neverShowModal(newVal) {
      localStorage.setItem('neverShowModal', newVal);
    }
  },
  methods: {
    // Helper to create a promise-based delay
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    resize() {
      const footerHeight = 60;
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight - footerHeight;
      if (this.mathAttackActive) {
        // Regenerate bubbles asynchronously on resize
        this.loadBubbleArrangement();
      }
    },
   animate() {
  // Ensure canvas and context are available
  if (!this.canvas || !this.ctx) {
    this.animationFrame = requestAnimationFrame(() => this.animate());
    return;
  }
  
  if (!this.celebrationActive) {
    // Create a smoky, fading trail effect
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Update and draw each particle
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      particle.speedX += this.gravityX;
      particle.speedY += this.gravityY;
      this.drawParticle(particle);
      // Remove particle if it's nearly invisible or too small
      if (particle.alpha <= 0.05 || particle.size < 0.5) {
        this.particles.splice(i, 1);
      }
    }
    
    // Update hue for particle colors
    this.hue = (this.hue + 1) % 360;

    // Draw bubbles on top if math attack is active and bubbles are loaded
    if (this.mathAttackActive && !this.loadingBubbles && this.bubbles.length > 0) {
      this.drawBubbles();
    }

    // Apply an optional screen shake effect if enabled
    if (this.shakeActive && this.canvas) {
      const shakeIntensity = 10; // Adjust intensity as needed.
      this.canvas.style.transform = `translate(${(Math.random() - 0.5) * shakeIntensity}px, ${(Math.random() - 0.5) * shakeIntensity}px)`;
    } else if (this.canvas) {
      this.canvas.style.transform = '';
    }
  }
  
  // Request the next frame using an arrow function to preserve context.
  this.animationFrame = requestAnimationFrame(() => this.animate());
},
    createParticle(x, y, pressure = 1) {
      let size = Math.random() * 15 + 5 * pressure;
      let speedX = (Math.random() * 3 - 1.5) * pressure;
      let speedY = (Math.random() * 3 - 1.5) * pressure;
      let color = `hsla(${this.hue}, 100%, 50%, 0.8)`;
      if (Math.random() < (pressure > 1.5 ? 0.05 : 0.02)) {
        color = `hsla(50, 100%, 60%, 1)`;
        size *= 1.5;
      }
      return { x, y, size, speedX, speedY, color, alpha: 1 };
    },
drawParticle(particle) {
  this.ctx.fillStyle = particle.color;
  this.ctx.globalAlpha = particle.alpha;
  this.ctx.beginPath();
  this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
  this.ctx.fill();
  this.ctx.globalAlpha = 1;

  // Update particle properties.
  particle.x += particle.speedX;
  particle.y += particle.speedY;
  particle.alpha -= 0.02;
  particle.size *= 0.95;
},
    createBubbleSound(frequency) {
      if (!this.audioContext) return;
      const now = this.audioContext.currentTime;
      if (now - this.lastSoundTime < 0.09) return;
  
      const oscillator = this.audioContext.createOscillator();
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(frequency, now);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.5, now + 0.05);
      oscillator.detune.setValueAtTime((Math.random() - 0.5) * 20, now);
  
      const bufferSize = this.audioContext.sampleRate * 0.05;
      const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        noiseData[i] = Math.random() * 2 - 1;
      }
      const noiseSource = this.audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = false;
  
      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.setValueAtTime(frequency / 2, now);
      filter.Q.setValueAtTime(1, now);
  
      const gainNode = this.audioContext.createGain();
      gainNode.gain.setValueAtTime(1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
  
      const merger = this.audioContext.createGain();
      oscillator.connect(merger);
      noiseSource.connect(merger);
      merger.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
  
      oscillator.start(now);
      noiseSource.start(now);
      oscillator.stop(now + 0.05);
      noiseSource.stop(now + 0.05);
  
      this.lastSoundTime = now;
    },
    handleDeviceOrientation(e) {
      const factor = 0.05;
      this.gravityX = ((e.gamma || 0) / 90) * factor;
      this.gravityY = ((e.beta || 0) / 90) * factor;
    },
    // Asynchronously load the bubble arrangement without overlap at vertices
async loadBubbleArrangement() {
  // Prevent multiple concurrent updates
  if (this.loadingBubbles) return;
  this.loadingBubbles = true;
  // Clear the previous arrangement and any lingering drawings
  this.bubbles = [];
  this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  await this.delay(100);
  
  const symbols = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "-", "∞"];
  let shuffled = symbols.slice().sort(() => Math.random() - 0.5);
  const availableShapes = [
    "Rectangle", "Square", "Circle", "Parallelogram",
    "Rhombus", "Ellipse",
    "Oval", "Kite", "Star"
  ];
  const chosenShape = availableShapes[Math.floor(Math.random() * availableShapes.length)];
  const leftMargin = 50;
  const rightMargin = this.canvas.width - 50;
  const topMargin = 200;
  const bottomMargin = this.canvas.height - 50;
  const rectangleTypes = ["Rectangle", "Square", "Parallelogram", "Trapezium", "Rhombus", "Kite"];
  const shapeSides = {
    "Triangle": 3,
    "Pentagon": 5,
    "Hexagon": 6,
    "Heptagon": 7,
    "Octagon": 8,
    "Nonagon": 9,
    "Decagon": 10,
    "Dodecagon": 12,
    "Tetrahedron": 3 // Treated as a triangle in 2D
  };
  const bubbleCount = shuffled.length;
  const minDistance = 2 * this.bubbleRadius;

  if (rectangleTypes.includes(chosenShape)) {
    // Arrange bubbles along a rectangle perimeter.
    // Use (i + 0.5)/bubbleCount to avoid exact corners.
    const requiredPerimeter = bubbleCount * minDistance;
    const maxWidth = rightMargin - leftMargin;
    const maxHeight = bottomMargin - topMargin;
    const perimeterRatio = requiredPerimeter / (2 * (maxWidth * 0.8 + maxHeight * 0.8));
    const scale = perimeterRatio > 1 ? 1 / perimeterRatio : 1;
    const arrWidth = maxWidth * 0.8 * scale;
    const arrHeight = maxHeight * 0.8 * scale;
    const offsetX = leftMargin + (maxWidth - arrWidth) / 2;
    const offsetY = topMargin + (maxHeight - arrHeight) / 2;
    const perimeter = 2 * (arrWidth + arrHeight);

    for (let i = 0; i < bubbleCount; i++) {
      // Shift position fraction by 0.5 to avoid vertices
      let fraction = (i + 0.5) / bubbleCount;
      let distance = fraction * perimeter;
      let x, y;
      if (distance <= arrWidth) {
        x = offsetX + distance;
        y = offsetY;
      } else if (distance <= arrWidth + arrHeight) {
        x = offsetX + arrWidth;
        y = offsetY + (distance - arrWidth);
      } else if (distance <= 2 * arrWidth + arrHeight) {
        x = offsetX + arrWidth - (distance - (arrWidth + arrHeight));
        y = offsetY + arrHeight;
      } else {
        x = offsetX;
        y = offsetY + arrHeight - (distance - (2 * arrWidth + arrHeight));
      }
      if (y + this.bubbleRadius > this.canvas.height - 50) {
        y = this.canvas.height - 50 - this.bubbleRadius;
      }
      this.bubbles.push({
        x, y,
        radius: this.bubbleRadius,
        symbol: shuffled[i],
        activated: false,
        shape: chosenShape
      });
    }
  } else if (chosenShape in shapeSides) {
    // Arrange bubbles along a regular polygon's perimeter.
    const N = shapeSides[chosenShape];
    const centerX = (leftMargin + rightMargin) / 2;
    const centerY = (topMargin + bottomMargin) / 2;
    const requiredPerimeter = bubbleCount * minDistance;
    const sideLength = requiredPerimeter / N;
    const radius = sideLength / (2 * Math.sin(Math.PI / N));
    const maxRadius = Math.min(rightMargin - leftMargin, bottomMargin - topMargin) * 0.4;
    const finalRadius = Math.min(radius, maxRadius);
    const vertices = [];
    
    for (let k = 0; k < N; k++) {
      let angle = (2 * Math.PI * k) / N;
      vertices.push({
        x: centerX + finalRadius * Math.cos(angle),
        y: centerY + finalRadius * Math.sin(angle)
      });
    }
    
    for (let i = 0; i < bubbleCount; i++) {
      // Use an offset fraction to keep bubble centers away from vertices.
      let f = (i + 0.5) / bubbleCount;
      let sideIndex = Math.floor(f * N);
      let t = (f * N) - sideIndex;
      let x = (1 - t) * vertices[sideIndex].x + t * vertices[(sideIndex + 1) % N].x;
      let y = (1 - t) * vertices[sideIndex].y + t * vertices[(sideIndex + 1) % N].y;
      if (y + this.bubbleRadius > this.canvas.height - 50) {
        y = this.canvas.height - 50 - this.bubbleRadius;
      }
      this.bubbles.push({
        x, y,
        radius: this.bubbleRadius,
        symbol: shuffled[i],
        activated: false,
        shape: chosenShape
      });
    }
  } else {
    // For circle and similar shapes, the standard even spacing works well.
    const centerX = (leftMargin + rightMargin) / 2;
    const centerY = (topMargin + bottomMargin) / 2;
    const theta = 2 * Math.PI / bubbleCount;
    const requiredRadius = minDistance / theta;
    const maxRadius = Math.min(rightMargin - leftMargin, bottomMargin - topMargin) * 0.4;
    const finalRadius = Math.max(requiredRadius, maxRadius);
    
    for (let i = 0; i < bubbleCount; i++) {
      let angle = (2 * Math.PI / bubbleCount) * i;
      let x = centerX + finalRadius * Math.cos(angle);
      let y = centerY + finalRadius * Math.sin(angle);
      if (y + this.bubbleRadius > this.canvas.height - 50) {
        y = this.canvas.height - 50 - this.bubbleRadius;
      }
      this.bubbles.push({
        x, y,
        radius: this.bubbleRadius,
        symbol: shuffled[i],
        activated: false,
        shape: chosenShape
      });
    }
  }
  this.loadingBubbles = false;
},
    drawBubbles() {
      this.ctx.save();
      const footerLimit = this.canvas.height - 50;
      this.bubbles.forEach(bubble => {
        if (bubble.y + bubble.radius > footerLimit) {
          bubble.y = footerLimit - bubble.radius;
        }
        this.ctx.save();
        this.drawBubbleShape(bubble);
        this.ctx.fillStyle = "#fff";
        this.ctx.font = "bold 20px sans-serif";
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.fillText(bubble.symbol, bubble.x, bubble.y);
        this.ctx.restore();
      });
      this.ctx.restore();
    },
    drawBubbleShape(bubble) {
      const { x, y, radius: r, shape } = bubble;
      this.ctx.beginPath();
      const fillStyle = "rgba(0, 0, 0, 0.1)";
      const strokeStyle = "rgba(255, 255, 255, 0.5)";
      const lineWidth = 1;
      switch (shape) {
        case "Circle":
          this.ctx.arc(x, y, r, 0, Math.PI * 2);
          break;
        case "Rectangle":
        case "Square":
          this.ctx.rect(x - r, y - r, 2 * r, 2 * r);
          break;
       
        
        case "Ellipse":
          this.ctx.ellipse(x, y, r, r * 0.7, 0, 0, Math.PI * 2);
          break;
        case "Oval":
          this.ctx.ellipse(x, y, r, r * 0.6, 0, 0, Math.PI * 2);
          break;
        case "Parallelogram":
          this.ctx.moveTo(x - r, y - r * 0.5);
          this.ctx.lineTo(x + r, y - r * 0.5);
          this.ctx.lineTo(x + r * 0.8, y + r * 0.5);
          this.ctx.lineTo(x - r * 0.8, y + r * 0.5);
          break;
        case "Rhombus":
          this.ctx.moveTo(x, y - r);
          this.ctx.lineTo(x + r, y);
          this.ctx.lineTo(x, y + r);
          this.ctx.lineTo(x - r, y);
          break;
        case "Trapezium":
          this.ctx.moveTo(x - r, y - r * 0.6);
          this.ctx.lineTo(x + r, y - r * 0.6);
          this.ctx.lineTo(x + r * 0.6, y + r * 0.6);
          this.ctx.lineTo(x - r * 0.6, y + r * 0.6);
          break;
        case "Kite":
          this.ctx.moveTo(x, y - r);
          this.ctx.lineTo(x + r * 0.6, y);
          this.ctx.lineTo(x, y + r * 0.6);
          this.ctx.lineTo(x - r * 0.6, y);
          break;
        case "Star":
          this.drawStar(x, y, r, 5, r / 2);
          return;
        default:
          this.ctx.arc(x, y, r, 0, Math.PI * 2);
          break;
      }
      this.ctx.closePath();
      this.ctx.fillStyle = fillStyle;
      this.ctx.fill();
      this.ctx.lineWidth = lineWidth;
      this.ctx.strokeStyle = strokeStyle;
      this.ctx.stroke();
    },
    drawRegularPolygon(x, y, radius, sides) {
      const angleStep = (2 * Math.PI) / sides;
      this.ctx.beginPath();
      this.ctx.moveTo(x + radius * Math.cos(0), y + radius * Math.sin(0));
      for (let i = 1; i <= sides; i++) {
        const angle = i * angleStep;
        this.ctx.lineTo(x + radius * Math.cos(angle), y + radius * Math.sin(angle));
      }
      this.ctx.closePath();
      this.ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
      this.ctx.fill();
      this.ctx.lineWidth = 1;
      this.ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      this.ctx.stroke();
    },
    drawStar(cx, cy, outerRadius, points, innerRadius) {
      let angle = Math.PI / 2 * 3;
      const step = Math.PI / points;
      this.ctx.beginPath();
      this.ctx.moveTo(cx, cy - outerRadius);
      for (let i = 0; i < points; i++) {
        let x = cx + Math.cos(angle) * outerRadius;
        let y = cy + Math.sin(angle) * outerRadius;
        this.ctx.lineTo(x, y);
        angle += step;
        x = cx + Math.cos(angle) * innerRadius;
        y = cy + Math.sin(angle) * innerRadius;
        this.ctx.lineTo(x, y);
        angle += step;
      }
      this.ctx.closePath();
      this.ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
      this.ctx.fill();
      this.ctx.lineWidth = 2;
      this.ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      this.ctx.stroke();
    },
    getBubbleAt(x, y) {
      for (let i = 0; i < this.bubbles.length; i++) {
        let bubble = this.bubbles[i];
        let dx = x - bubble.x;
        let dy = y - bubble.y;
        if (Math.sqrt(dx * dx + dy * dy) <= bubble.radius) {
          return bubble;
        }
      }
      return null;
    },
    handleTouchStart(e) {
      e.preventDefault();
      this.touchActive = true;
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      if (navigator.vibrate) {
        navigator.vibrate([10, 2, 5]);
      }
      const touch = e.touches[0];
      const x = touch.clientX;
      const y = touch.clientY;
      const pressure = touch.force || 1;
      this.addParticles(x, y, pressure);
      if (this.mathAttackActive) {
        const bubble = this.getBubbleAt(x, y);
        if (bubble && !bubble.activated) {
          this.userInput += bubble.symbol;
          bubble.activated = true;
          this.createBubbleSound(400);
        }
      }
    },
    handleTouchMove(e) {
      e.preventDefault();
      if (this.touchActive) {
        const touch = e.touches[0];
        const x = touch.clientX;
        const y = touch.clientY;
        const pressure = touch.force || 1;
        this.addParticles(x, y, pressure);
        if (navigator.vibrate) {
          let intensity = Math.random();
          let vibIntensity = Math.floor(3 + intensity * 5);
          navigator.vibrate(vibIntensity);
        }
        if (this.mathAttackActive) {
          const bubble = this.getBubbleAt(x, y);
          if (bubble && !bubble.activated) {
            this.userInput += bubble.symbol;
            bubble.activated = true;
            this.createBubbleSound(400);
          }
        }
      }
    },
    handleTouchEnd(e) {
      e.preventDefault();
      this.touchActive = false;
      if (this.mathAttackActive && this.userInput) {
        if (this.userInput === this.currentAnswer) {
          this.handleCorrectAnswer();
        } else {
          this.userInput = "";
          this.createBubbleSound(100);
        }
      }
      this.resetBubbleActivation();
      this.createBubbleSound(1500);
      this.createBubbleSound(800);
      if (navigator.vibrate) {
        navigator.vibrate([5, 10, 5]);
      }
    },
    resetBubbleActivation() {
      this.bubbles.forEach(bubble => {
        bubble.activated = false;
      });
    },
    addParticles(x, y, pressure = 1) {
      const count = Math.floor(5 * pressure);
      for (let i = 0; i < count; i++) {
        this.particles.push(this.createParticle(x, y, pressure));
      }
    },
    handleMathAttackSwitchChange() {
      if (this.mathAttackActive) {
        this.showDifficultyModal = true;
      } else {
        this.endMathAttack();
      }
    },
    selectDifficulty(level) {
      this.mathAttackLevel = level;
      if (level === 'easy') {
        this.questionDuration = 20;
      } else if (level === 'medium') {
        this.questionDuration = 10;
      } else if (level === 'hard') {
        this.questionDuration = 7.5;
      } else if (level === 'impossible') {
        this.questionDuration = 5;
      }
      this.showDifficultyModal = false;
      this.startMathAttack();
    },
    async startMathAttack() {
      this.mathAttackActive = true;
      this.sessionStartTime = Date.now();
      this.currentTime = Date.now();
      this.mathAttackScore = 0;
      this.userInput = '';
      this.generateMathQuestion();
      await this.loadBubbleArrangement();
      this.questionStartTime = Date.now();
      if (this.mathTimerInterval) clearInterval(this.mathTimerInterval);
      this.mathTimerInterval = setInterval(() => {
        this.currentTime = Date.now();
        let sessionElapsed = (this.currentTime - this.sessionStartTime) / 1000;
        if (sessionElapsed >= this.sessionDuration) {
          this.endMathAttack();
        } else {
          let questionElapsed = (this.currentTime - this.questionStartTime) / 1000;
          if (questionElapsed >= this.questionDuration) {
            this.handleQuestionTimeout();
          }
        }
      }, 100);
    },
    endMathAttack() {
      clearInterval(this.mathTimerInterval);
      this.mathTimerInterval = null;
      this.mathAttackActive = false;
      this.celebrateScore(this.mathAttackScore, 0);
    },
    generateMathQuestion() {
  const operators = ['+', '-', '×', '÷'];
  let question = "";
  let answer = 0;

  const hasUniqueDigits = (str) => {
    const digits = str.replace(/[^0-9]/g, '');
    return new Set(digits).size === digits.length;
  };

  let valid = false;
  do {
    const op = operators[Math.floor(Math.random() * operators.length)];
    if (op === '+') {
      const a = Math.floor(Math.random() * 50);
      const b = Math.floor(Math.random() * 50);
      question = `${a} + ${b}`;
      answer = a + b;
    } else if (op === '-') {
      const a = Math.floor(Math.random() * 50);
      const b = Math.floor(Math.random() * 50);
      question = `${a} - ${b}`;
      answer = a - b;
    } else if (op === '×') {
      const a = Math.floor(Math.random() * 20);
      const b = Math.floor(Math.random() * 20);
      question = `${a} × ${b}`;
      answer = a * b;
    } else if (op === '÷') {
      const b = Math.floor(Math.random() * 9) + 1;
      const candidate = Math.floor(Math.random() * 20);
      const a = b * candidate;
      question = `${a} ÷ ${b}`;
      answer = candidate;
    }
    valid = hasUniqueDigits(answer.toString());
  } while (!valid);

  this.currentQuestion = question;
  this.currentAnswer = answer.toString();
},
    async handleCorrectAnswer() {
      this.mathAttackScore += 10;
      // Trigger celebration particles
      await this.hurrayParticles();
      if (this.mathAttackScore > this.highScore) {
        this.highScore = this.mathAttackScore;
      }
      await this.regenerateQuestion();
    },
    async handleQuestionTimeout() {
      await this.regenerateQuestion();
    },
    async regenerateQuestion() {
      await this.delay(this.questionDelay);
      let currentTime = Date.now();
      let sessionElapsed = (currentTime - this.sessionStartTime) / 1000;
      if (sessionElapsed < this.sessionDuration) {
        this.userInput = '';
        this.generateMathQuestion();
        await this.loadBubbleArrangement();
        this.questionStartTime = Date.now();
      }
    },
   async hurrayParticles() {
  this.celebrationParticles = [];
  const numParticles = 300;
  const duration = 3000;
  const startTime = Date.now();

  // Create particles with a smoky effect and vibrant colors
  for (let i = 0; i < numParticles; i++) {
    this.celebrationParticles.push({
      x: this.canvas.width / 2,
      y: this.canvas.height / 2,
      vx: (Math.random() - 0.5) * 4,
      vy: (Math.random() - 0.5) * 4,
      size: Math.random() * 8 + 5,
      alpha: Math.random() * 0.5 + 0.5,
      color: `hsl(${Math.random() * 360}, 100%, 70%)`
    });
  }

  // Screen shake effect
  const shakeIntensity = 10;
  const shakeDuration = duration;
    // eslint-disable-next-line
  const shakeInterval = 50;

  const runCelebration = () => {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Apply screen shake
    if (Date.now() - startTime < shakeDuration) {
      this.canvas.style.transform = `translate(${(Math.random() - 0.5) * shakeIntensity}px, ${(Math.random() - 0.5) * shakeIntensity}px)`;
    } else {
      this.canvas.style.transform = "";
    }

    // Animate particles with a smoky dispersion effect
    this.celebrationParticles.forEach(p => {
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.98; // Slow down over time
      p.vy *= 0.98;
      p.alpha *= 0.97; // Fade out

      this.ctx.fillStyle = p.color;
      this.ctx.globalAlpha = Math.max(p.alpha, 0.1);
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      this.ctx.fill();
    });

    if (Date.now() - startTime < duration) {
      requestAnimationFrame(runCelebration);
    } else {
      this.celebrationParticles = [];
      this.canvas.style.transform = "";
    }
  };

  runCelebration();
},
    celebrateScore(finalScore, bonus) {
      this.celebrationActive = true;
      this.celebrationParticles = [];
      for (let i = 0; i < 250; i++) {
        this.celebrationParticles.push({
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          vx: (Math.random() - 0.5) * 8,
          vy: (Math.random() - 0.5) * 8,
          size: Math.random() * 5 + 2,
          alpha: 1,
          color: `hsl(${Math.random() * 360}, 100%, 60%)`
        });
      }
      const startTime = Date.now();
      const duration = 3000;
      const runCelebration = () => {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.celebrationParticles.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;
          p.alpha -= 0.01;
          this.ctx.fillStyle = p.color;
          this.ctx.globalAlpha = Math.max(p.alpha, 0);
          this.ctx.beginPath();
          this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          this.ctx.fill();
        });
        this.ctx.globalAlpha = 1;
        this.ctx.font = 'bold 64px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.fillStyle = '#FFD700';
        this.ctx.fillText(`Score: ${finalScore}`, this.canvas.width / 2, this.canvas.height / 2 - 20);
        this.ctx.font = 'bold 32px sans-serif';
        this.ctx.fillStyle = '#00FFFF';
        this.ctx.fillText(`Bonus: ${bonus}`, this.canvas.width / 2, this.canvas.height / 2 + 30);
        if (Date.now() - startTime < duration) {
          requestAnimationFrame(runCelebration);
        } else {
          this.celebrationActive = false;
          this.celebrationParticles = [];
        }
      };
      runCelebration();
    },
    scheduleModalTips() {
      if (this.neverShowModal) return;
      this.modalInterval = setInterval(() => {
        if (!this.mathAttackActive && !this.showFeatureModal) {
          this.showFeatureModal = true;
        }
      }, 15000);
    },
    clearModalTips() {
      clearInterval(this.modalInterval);
      this.modalInterval = null;
      this.showFeatureModal = false;
    },
    closeModalTip() {
      this.showFeatureModal = false;
    },
    neverShowTips() {
      this.neverShowModal = true;
      this.clearModalTips();
    }
  },
  mounted() {
    const stored = localStorage.getItem('neverShowModal');
    if (stored) {
      this.neverShowModal = stored === 'true';
    }
    this.canvas = this.$refs.canvas;
    this.ctx = this.canvas.getContext('2d');
    this.resize();
    window.addEventListener('resize', this.resize);
    window.addEventListener('deviceorientation', this.handleDeviceOrientation);
    this.animate();
    this.canvas.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.canvas.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.canvas.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    setTimeout(() => { this.showHint = false; }, 5000);
    if (!this.mathAttackActive && !this.neverShowModal) {
      this.scheduleModalTips();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('deviceorientation', this.handleDeviceOrientation);
    cancelAnimationFrame(this.animationFrame);
    this.canvas.removeEventListener('touchstart', this.handleTouchStart);
    this.canvas.removeEventListener('touchmove', this.handleTouchMove);
    this.canvas.removeEventListener('touchend', this.handleTouchEnd);
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.clearModalTips();
  }
};
</script>

<style scoped>
.container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  background: #000;
  touch-action: none;
  overflow: hidden;
}
canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  z-index: 1;
}
.hint {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 18px;
  padding: 10px 20px;
  user-select: none;
  z-index: 1000;
  pointer-events: none;
}
.controls {
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}
.switch input:checked + .slider {
  background-color: #2196F3;
}
.switch input:checked + .slider:before {
  transform: translateX(26px);
}
.switch-label {
  margin-left: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 16px;
  color: #fff;
}
.math-attack-info {
  background: rgba(0, 0, 0, 0.6);
  padding: 12px 18px;
  border-radius: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #fff;
}
.session-time, .question-time {
  font-size: 18px;
  margin-bottom: 5px;
}
.question-board {
  font-size: 20px;
  margin-bottom: 5px;
}
.score, .highscore, .user-input {
  font-size: 18px;
}
.progress-container {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  overflow: hidden;
  height: 20px;
  width: 250px;
  margin-top: 10px;
}
.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #00FFFF, #FF00FF);
  transition: width 0.2s ease;
}
  .math-attack-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 900px;
  margin: auto;
  padding: 20px;
  background: rgba(20, 20, 20, 0.9);
  border-radius: 10px;
  box-shadow: 0px 0px 15px rgba(0, 255, 255, 0.5);
  font-family: "Orbitron", sans-serif;
  color: #00ffff;
  text-shadow: 0 0 5px #00ffff, 0 0 10px #008080;
}

/* Upper row layout */
.math-attack-info > div {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Session time (Upper Left) */
.session-time {
  font-size: 12px;
  font-weight: bold;
  color: #ff6666;
  text-shadow: 0 0 5px #ff3333;
}

/* Question in the Middle */
.question-board {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
}

/* Progress Bar Container */
.progress-container {
  width: 100%;
  max-width: 500px;
  margin-top: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar {
  height: 10px;
  background: linear-gradient(to right, #00ff99, #0099ff);
  transition: width 0.3s ease-in-out;
}

.progress-label {
  font-size: 10px;
  text-align: center;
  margin-top: 5px;
  color: #ffffff;
  text-align: center;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Score & High Score (Upper Right) */
.score,
.highscore {
  font-size: 12px;
  font-weight: bold;
  color: #ffcc00;
  text-shadow: 0 0 5px #ffcc00;
}

/* Answer (Big & Prominent) */
.user-input {
  font-size: 28px;
  font-weight: bold;
  color: #00ff00;
  text-shadow: 0 0 10px #00ff00;
  margin-top: 20px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .math-attack-info {
    padding: 15px;
  }

  .question-board {
    font-size: 18px;
  }

  .user-input {
    font-size: 22px;
  }

  .score,
  .highscore,
  .session-time {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .math-attack-info {
    flex-direction: column;
    text-align: center;
  }

  .question-board {
    font-size: 16px;
  }

  .user-input {
    font-size: 20px;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100vh - 60px);
  background: rgba(0, 0, 0, 0.7);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #222;
  color: #fff;
  padding: 20px 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 90%;
}
.modal-content h2 {
  margin-bottom: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.modal-content p {
  margin-bottom: 20px;
  font-size: 16px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.modal-buttons button, .modal-content button {
  background: #2196F3;
  border: none;
  padding: 10px 15px;
  margin: 0 5px;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  transition: background 0.3s;
}
.modal-buttons button:hover, .modal-content button:hover {
  background: #1976D2;
}
.difficulty-selection-modal {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}
.difficulty-selection-modal button {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 16px;
  padding: 8px 12px;
  background-color: #444;
  border: none;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s;
}
.difficulty-selection-modal button:hover {
  background-color: #666;
}
</style>
